import _ from 'underscore';
import PropTypes from 'prop-types';
import ProposalTableColumns from 'apps/proposals/components/proposal-table-columns';
import { useCachedContactByIdsResolver } from '@evertrue/vms-insights';
import { FlexTable } from '@evertrue/sdk';
import { useIdentity } from 'base/identity-resolver';
import Decorator from 'clientDecorator';
import { useCallback, useMemo, useState } from 'react';

const ProposalTable = ({
  proposals = {},
  loading = false,
  limit = 0,
  total = 0,
  page = 0,
  handlePageChange = () => {},
  handlePageSizeChange = () => {},
  sortProp = '',
  sortReverse = false,
  onSort = () => {},
}) => {
  const { user, org } = useIdentity();
  const contactId = Decorator.User.getContactId(user, org.id);

  const [paginationModel, setPaginationModel] = useState({
    page: page - 1 || 0,
    pageSize: limit,
  });

  const handlePaginationModelChange = useCallback(
    (model) => {
      setPaginationModel(model);
      handlePageChange(model.page + 1);
      handlePageSizeChange(model.pageSize);
    },
    [handlePageChange, handlePageSizeChange]
  );

  const sortModel = useMemo(() => {
    return [
      {
        field: sortProp,
        sort: sortReverse ? 'desc' : 'asc',
      },
    ];
  }, [sortProp, sortReverse]);

  const handleSortModelChange = useCallback(
    (newModel) => {
      if (newModel.length === 0) return;

      const { field, sort } = newModel[0];
      const reverse = sort === 'desc';

      onSort(field, reverse);
    },
    [onSort]
  );

  const contact_ids =
    proposals.items &&
    proposals.items.flatMap((proposal) => {
      return proposal.contacts.map((i) => i.contact_id);
    });

  // prefetch list of contact_ids that will be accessed later in the tableCells
  useCachedContactByIdsResolver(_.uniq(contact_ids));

  return (
    <div style={{ height: 'calc(100vh - 155px)', overflowY: 'auto', width: '100%' }}>
      <FlexTable
        tableKey="proposal-table"
        columns={ProposalTableColumns(contactId)}
        rows={proposals.items ? proposals.items : []}
        emptyStateMessage="No results match your current filters"
        loading={loading}
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        getRowHeight={() => 'auto'}
        autosizeOptions={{
          columns: [
            'proposal',
            'prospect',
            'original_ask_amount',
            'expected_amount',
            'stage',
            'status_start_date',
            'solicitors',
            'ask_date',
            'created_date',
            'expected_date',
            'funded_date',
          ],
          includeOutliers: true,
          includeHeaders: true,
          expand: true,
        }}
      />
    </div>
  );
};

ProposalTable.propTypes = {
  proposals: PropTypes.object,
  loading: PropTypes.bool,
  limit: PropTypes.number,
  total: PropTypes.number,
  page: PropTypes.number,
  handlePageChange: PropTypes.func,
  handlePageSizeChange: PropTypes.func,
  sortProp: PropTypes.string,
  sortReverse: PropTypes.bool,
  onSort: PropTypes.func,
};

export default ProposalTable;
